/**
 * Hooks React Query pour SavV2
 * ✅ Conforme CONETEXT.md : Pas de useEffect pour data fetching
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { savV2CompleteApi } from '../../../../Api/sav-v2/savV2CompleteApi';
import { savV2InterventionApi } from '../../../../Api/sav-v2/savV2InterventionApi';
import { contractApi } from '../../../../Api/contracts/contractApi';

/**
 * Hook pour charger toutes les données du SAV en une seule requête
 */
export function useSavV2FullData(savId: number) {
  return useQuery({
    queryKey: ['sav-v2', savId, 'full'],
    queryFn: async () => {
      const [fullSavData, statusesData] = await Promise.all([
        savV2CompleteApi.getFullSavData(savId),
        savV2CompleteApi.getVirtualStatuses(savId)
      ]);
      
      return {
        sav: fullSavData,
        contract: fullSavData.contract,
        quotes: fullSavData.quotes || [],
        events: fullSavData.events || [],
        virtualStatuses: statusesData.virtualStatuses || []
      };
    },
    staleTime: 1000 * 30, // 30 secondes
  });
}

/**
 * Hook pour charger les interventions d'un SAV
 */
export function useSavV2Interventions(savId: number) {
  return useQuery({
    queryKey: ['sav-v2', savId, 'interventions'],
    queryFn: () => savV2InterventionApi.getBySavId(savId),
    staleTime: 1000 * 30, // 30 secondes
  });
}

/**
 * Hook pour charger le plafond financier du contrat
 */
export function useContractFinancialCap(contractId: number | undefined) {
  return useQuery({
    queryKey: ['contract', contractId, 'financial-cap'],
    queryFn: () => contractApi.getFinancialCapUsage(contractId!),
    enabled: !!contractId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Hook pour invalider toutes les données SavV2
 */
export function useInvalidateSavV2() {
  const queryClient = useQueryClient();
  
  return (savId: number) => {
    queryClient.invalidateQueries({ queryKey: ['sav-v2', savId] });
  };
}

